import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSendLeadEmail } from "@/hooks/useSendLeadEmail";
import { useResolvedEmailTemplate } from "@/hooks/useEmailTemplate";
import { PRODUCTION_DOMAIN } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const LEAD_INBOUND_DOMAIN = "leads.creativeinvestments.africa";

interface SendQuoteEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  clientEmail: string;
  clientName: string;
  leadInboundEmailId?: string | null;
  onSuccess: () => void;
}

export const SendQuoteEmailDialog = ({
  open,
  onOpenChange,
  leadId,
  clientEmail,
  clientName,
  leadInboundEmailId,
  onSuccess,
}: SendQuoteEmailDialogProps) => {
  const sendEmailMutation = useSendLeadEmail();
  
  // Fetch quote and brand details
  const { data: quoteData } = useQuery({
    queryKey: ["quote-email-data", leadId],
    queryFn: async () => {
      // Get latest quotation
      const { data: quotation } = await supabase
        .from("quotations")
        .select("share_token, quote_number, total_amount, valid_until")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // Get lead with brand info and currency
      const { data: lead } = await supabase
        .from("leads")
        .select("brand_id, title, currency, business_brands(name, email)")
        .eq("id", leadId)
        .maybeSingle();
      
      return { quotation, lead };
    },
    enabled: open,
  });

  const brandName = quoteData?.lead?.business_brands?.name || "Our Team";
  const quoteNumber = quoteData?.quotation?.quote_number || "";
  const shareToken = quoteData?.quotation?.share_token || "";
  const totalAmount = quoteData?.quotation?.total_amount;
  const currency = quoteData?.lead?.currency || "USD";
  const validUntil = quoteData?.quotation?.valid_until;
  const leadTitle = quoteData?.lead?.title || "Your Travel Quote";
  
  // Generate quote link using production domain
  const quoteLink = shareToken 
    ? `${PRODUCTION_DOMAIN}/quote/${shareToken}`
    : "";

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toRecipients, setToRecipients] = useState<string[]>([]);
  const [toInput, setToInput] = useState("");
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState("");

  // Compute the lead email address for CC
  const leadEmailAddress = leadInboundEmailId 
    ? `lead-${leadInboundEmailId}@${LEAD_INBOUND_DOMAIN}` 
    : null;

  // Initialize recipients when dialog opens
  useEffect(() => {
    if (open) {
      setToRecipients(clientEmail ? [clientEmail] : []);
      setToInput("");
      // Auto-fill CC with lead email address so replies come to the dashboard
      setCcRecipients(leadEmailAddress ? [leadEmailAddress] : []);
      setCcInput("");
    }
  }, [open, clientEmail, leadEmailAddress]);

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && !toRecipients.includes(trimmed) && trimmed.includes("@")) {
      setToRecipients([...toRecipients, trimmed]);
      setToInput("");
    }
  };

  const removeEmail = (email: string) => {
    setToRecipients(toRecipients.filter((e) => e !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail(toInput);
    }
  };

  const addCcEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && !ccRecipients.includes(trimmed) && trimmed.includes("@")) {
      setCcRecipients([...ccRecipients, trimmed]);
      setCcInput("");
    }
  };

  const removeCcEmail = (email: string) => {
    setCcRecipients(ccRecipients.filter((e) => e !== email));
  };

  const handleCcKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCcEmail(ccInput);
    }
  };

  // Pre-fill from the admin-editable default wording (Admin → Email Templates)
  const formattedTotal = totalAmount
    ? new Intl.NumberFormat("en-US", { style: "currency", currency }).format(totalAmount)
    : "";
  const formattedValidUntil = validUntil ? new Date(validUntil).toLocaleDateString() : "";

  const resolved = useResolvedEmailTemplate(
    "quote_share",
    quoteData?.lead?.brand_id ?? null,
    {
      client_name: clientName || "Valued Client",
      brand_name: brandName,
      trip_title: leadTitle,
      quote_number: quoteNumber,
      total: formattedTotal,
      valid_until: formattedValidUntil,
      link: quoteLink,
    },
    open,
  );

  useEffect(() => {
    if (open && quoteData && !resolved.isLoading) {
      setSubject(resolved.subject);
      setBody(resolved.body);
    }
  }, [open, quoteData, resolved.isLoading, resolved.subject, resolved.body]);

  const handleSend = async () => {
    if (toRecipients.length === 0) return;

    // Convert plain text to HTML
    const htmlBody = body
      .split("\n")
      .map(line => {
        if (line.startsWith("http")) {
          return `<p><a href="${line}" style="color: #2563eb; text-decoration: underline;">${line}</a></p>`;
        }
        return line ? `<p>${line}</p>` : "<br/>";
      })
      .join("");

    await sendEmailMutation.mutateAsync({
      leadId,
      to: toRecipients,
      cc: ccRecipients.length > 0 ? ccRecipients : undefined,
      subject,
      bodyText: body,
      bodyHtml: `<div style="font-family: sans-serif; line-height: 1.6;">${htmlBody}</div>`,
    });

    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Send Quote via Email</DialogTitle>
          <DialogDescription>
            Review and customize the email before sending to the client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="to">To</Label>
            <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-[42px] bg-background">
              {toRecipients.map((email) => (
                <Badge key={email} variant="secondary" className="gap-1">
                  {email}
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Input
                id="to"
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={() => toInput && addEmail(toInput)}
                placeholder={toRecipients.length === 0 ? "Add recipient..." : "Add another..."}
                className="flex-1 min-w-[120px] border-0 p-0 h-6 focus-visible:ring-0 shadow-none"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Press Enter or comma to add additional recipients
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc">CC</Label>
            <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-[42px] bg-background">
              {ccRecipients.map((email) => (
                <Badge key={email} variant="secondary" className="gap-1">
                  {email}
                  <button
                    type="button"
                    onClick={() => removeCcEmail(email)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Input
                id="cc"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={handleCcKeyDown}
                onBlur={() => ccInput && addCcEmail(ccInput)}
                placeholder={ccRecipients.length === 0 ? "Add CC recipient..." : "Add another..."}
                className="flex-1 min-w-[120px] border-0 p-0 h-6 focus-visible:ring-0 shadow-none"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Lead email is auto-included so replies come to the dashboard
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Message</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email content"
              rows={12}
              className="font-mono text-sm"
            />
          </div>

          {quoteLink && (
            <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
              <strong>Quote Link:</strong>{" "}
              <a 
                href={quoteLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {quoteLink}
              </a>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendEmailMutation.isPending || !subject || !body || toRecipients.length === 0}
            className="gap-2"
          >
            {sendEmailMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
